import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, IconButton } from '@mui/material';
import { Send, Trash2, Sparkles } from 'lucide-react';
import CommonDrawerLayout from '../CommonDrawerLayout';
import CommonTabs from '../CommonTabs';
import CommonLoader from '../CommonLoader';
import ConfirmActionModal from '../ConfirmActionModal';
import HoverTooltip from '../hoverOverlay/HoverTooltip';
import { toast } from '../Toast/ToastUtils';
import AiMessageBubble from './AiMessageBubble';
import AiContextPanel from './AiContextPanel';
import AiThinkingIndicator from './AiThinkingIndicator';
import AiStreamingMessage from './AiStreamingMessage';
import { fetchAiThread, sendAiMessage, clearAiThread, resetAi, selectAiContext, selectAiMessages, selectAiConfigured, selectAiThreadLoading, selectAiThreadError, selectAiSending, selectAiStreaming, } from '../../redux/aiSlice';
/** Prompts offered on an empty composer — the common follow-ups after an analysis. */
const QUICK_PROMPTS = [
    'Why is the click-through rate underperforming?',
    'Write three headline variations to test next.',
    'What should I change on the landing page?',
    'Is this creative fatiguing?',
];
const AiSuggestionsDrawer = ({ open, onClose, clientId, adId, adName, category }) => {
    const dispatch = useDispatch();
    const context = useSelector(selectAiContext);
    const messages = useSelector(selectAiMessages);
    const configured = useSelector(selectAiConfigured);
    const loading = useSelector(selectAiThreadLoading);
    const error = useSelector(selectAiThreadError);
    const sending = useSelector(selectAiSending);
    const streaming = useSelector(selectAiStreaming);
    const [tab, setTab] = useState('chat');
    const [draft, setDraft] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [clearing, setClearing] = useState(false);
    // The in-flight user turn lives here rather than in Redux, so a failed send
    // can't strand a phantom message in the store.
    const [pendingText, setPendingText] = useState(null);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    // Guards the auto-analysis against StrictMode's double-effect and against
    // re-firing when the thread reloads.
    const kickedOff = useRef(null);
    // The dispatched turn, so closing the drawer can abort it. Leaving it running
    // holds the server's per-thread lock for the rest of its 45-90s, and reopening
    // inside that window used to come back 429 "still answering your last message".
    const inFlight = useRef(null);
    const params = useMemo(() => ({ clientId, adId, category }), [clientId, adId, category]);
    const threadId = `${clientId}:${adId}:${category}`;
    // Load context + stored conversation each time the drawer opens for an entity.
    useEffect(() => {
        if (open && clientId && adId) {
            setTab('chat');
            setDraft('');
            setPendingText(null);
            setConfirmOpen(false);
            // Cleared so a reopened ad re-runs the opening analysis against
            // freshly rebuilt context rather than reusing the last session's.
            kickedOff.current = null;
            dispatch(fetchAiThread(params));
        }
        else if (!open) {
            // Cancel any turn still running, then wipe the slice — so reopening
            // this ad starts clean instead of colliding with the previous request.
            inFlight.current?.abort();
            inFlight.current = null;
            dispatch(resetAi());
        }
    }, [open, clientId, adId, category, dispatch, params]);
    // Fresh thread → have the agent open with its analysis, unprompted.
    useEffect(() => {
        if (!open || loading || !context || !configured)
            return;
        if (messages.length > 0 || sending)
            return;
        if (kickedOff.current === threadId)
            return;
        kickedOff.current = threadId;
        inFlight.current = dispatch(sendAiMessage({ ...params, message: '' }));
    }, [open, loading, context, configured, messages.length, sending, threadId, dispatch, params]);
    // Keep the newest turn in view as the conversation grows — including while a
    // reply is still being written, which is when the box grows the most.
    useEffect(() => {
        const el = scrollRef.current;
        if (el)
            el.scrollTop = el.scrollHeight;
    }, [messages.length, sending, pendingText, streaming]);
    const submit = async (text) => {
        const value = text.trim();
        if (!value || sending)
            return;
        setDraft('');
        setPendingText(value);
        const promise = dispatch(sendAiMessage({ ...params, message: value }));
        inFlight.current = promise;
        const action = await promise;
        inFlight.current = null;
        setPendingText(null);
        // A cancel is the user closing the drawer — not something to apologise for.
        if (action?.error && action.payload !== 'cancelled') {
            toast.error(action.payload || 'Failed to generate a response.');
            // Hand the text back so the user doesn't lose what they typed.
            setDraft(value);
        }
    };
    const handleRefine = (s) => {
        setTab('chat');
        setDraft(`About "${s.title}" — `);
        // Focus after the tab swap paints, and drop the caret at the end.
        window.setTimeout(() => {
            const el = inputRef.current;
            if (!el)
                return;
            el.focus();
            el.setSelectionRange(el.value.length, el.value.length);
        }, 0);
    };
    /**
     * Deleting is irreversible and throws away a multi-minute analysis, so it is
     * confirmed first. On success the drawer closes rather than sitting on an
     * empty thread — closing dispatches resetAi, and reopening the ad rebuilds
     * the context from scratch and re-runs the analysis on fresh numbers.
     */
    const handleClear = async () => {
        setClearing(true);
        const action = await dispatch(clearAiThread(params));
        setClearing(false);
        if (action?.error) {
            toast.error(action.payload || 'Failed to clear the conversation.');
            return;
        }
        setConfirmOpen(false);
        toast.success('Conversation deleted.');
        onClose();
    };
    const onKeyDown = (e) => {
        // Enter sends, Shift+Enter breaks the line — the convention every chat
        // UI the user already has open follows.
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit(draft);
        }
    };
    const renderChat = () => (_jsxs(Box, { sx: { height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }, children: [_jsxs(Box, { ref: scrollRef, sx: { flex: 1, overflowY: 'auto', minHeight: 0, p: 2, display: 'flex', flexDirection: 'column', gap: 2, bgcolor: '#FCFCFD' }, children: [!configured && (_jsx(Box, { sx: { p: 1.5, borderRadius: '8px', bgcolor: '#FFFAEB', border: '1px solid #FEDF89' }, children: _jsx(Typography, { sx: { fontSize: '12.5px', color: '#B54708' }, children: "The AI agent is not configured on this environment yet. The ad data below was still retrieved \u2014 open the Data tab to review it." }) })), messages.map((m, i) => (_jsx(AiMessageBubble, { message: m, onRefine: handleRefine }, i))), pendingText && (_jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-end' }, children: _jsx(Box, { sx: { maxWidth: '82%', bgcolor: '#EEF4FF', border: '1px solid #E0EAFF', borderRadius: '12px 12px 2px 12px', px: 1.5, py: 1.25, opacity: 0.65 }, children: _jsx(Typography, { sx: { fontSize: '13.5px', color: '#101828', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }, children: pendingText }) }) })), sending && (streaming
                        ? _jsx(AiStreamingMessage, { text: streaming })
                        : _jsx(AiThinkingIndicator, { variant: pendingText ? 'reply' : 'analysis' }))] }), _jsxs(Box, { sx: { flexShrink: 0, borderTop: '1px solid #EAECF0', bgcolor: '#FFFFFF', p: 1.5 }, children: [!sending && !pendingText && messages.length > 0 && !draft && (_jsx(Box, { sx: { display: 'flex', flexWrap: 'wrap', gap: '6px', mb: 1 }, children: QUICK_PROMPTS.map((q) => (_jsx(Box, { component: "button", onClick: () => submit(q), sx: {
                                border: '1px solid #EAECF0', borderRadius: '16px', bgcolor: '#FFFFFF',
                                px: '10px', py: '4px', fontSize: '11.5px', color: '#475467',
                                cursor: 'pointer', '&:hover': { bgcolor: '#F9FAFB' },
                            }, children: q }, q))) })), _jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-end', gap: 1, border: '1px solid #D0D5DD', borderRadius: '8px', px: 1.25, py: 1, '&:focus-within': { borderColor: '#6172F3' } }, children: [_jsx(Box, { component: "textarea", ref: inputRef, value: draft, onChange: (e) => setDraft(e.target.value), onKeyDown: onKeyDown, disabled: !configured || sending, rows: 1, maxLength: 4000, placeholder: configured ? 'Ask the agent about this ad…' : 'AI agent unavailable', sx: {
                                    flex: 1, border: 'none', outline: 'none', resize: 'none', fontFamily: 'inherit',
                                    fontSize: '13.5px', color: '#101828', lineHeight: '20px', maxHeight: 120,
                                    bgcolor: 'transparent', '&::placeholder': { color: '#98A2B3' },
                                } }), _jsx(IconButton, { size: "small", onClick: () => submit(draft), disabled: !configured || sending || !draft.trim(), "aria-label": "Send message", sx: {
                                    bgcolor: draft.trim() && !sending ? '#6172F3' : '#F2F4F7',
                                    '&:hover': { bgcolor: draft.trim() && !sending ? '#4E5BD6' : '#F2F4F7' },
                                    width: 30, height: 30,
                                }, children: _jsx(Send, { size: 14, color: draft.trim() && !sending ? '#FFFFFF' : '#98A2B3' }) })] }), _jsx(Typography, { sx: { fontSize: '10.5px', color: '#98A2B3', mt: '6px' }, children: "Enter to send \u00B7 Shift+Enter for a new line. Suggestions are AI-generated \u2014 review before applying." })] })] }));
    const body = () => {
        if (loading) {
            return _jsx(Box, { sx: { height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(CommonLoader, { className: "table-loader" }) });
        }
        if (error || !context) {
            return (_jsx(Box, { sx: { height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }, children: _jsx(Typography, { sx: { color: '#B42318', fontSize: '13.5px', textAlign: 'center' }, children: error || 'No data available for this ad.' }) }));
        }
        return (_jsxs(Box, { sx: { height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }, children: [_jsxs(Box, { sx: { flexShrink: 0, px: 2, pt: 1.5, pb: 1, borderBottom: '1px solid #EAECF0' }, children: [_jsx(Typography, { sx: { fontSize: '13px', fontWeight: 600, color: '#101828', wordBreak: 'break-word', mb: 1 }, children: adName || context.ad.ad_name || 'Ad' }), _jsx(CommonTabs, { selectedValue: tab, onChange: setTab, fullWidth: true, tabs: [{ label: 'Suggestions', value: 'chat' }, { label: 'Data', value: 'data' }] })] }), _jsx(Box, { sx: { flex: 1, minHeight: 0, overflow: 'hidden' }, children: tab === 'chat'
                        ? renderChat()
                        : _jsx(Box, { sx: { height: '100%', overflowY: 'auto', p: 2, bgcolor: '#FCFCFD' }, children: _jsx(AiContextPanel, { context: context, clientId: clientId, category: category }) }) })] }));
    };
    return (_jsxs(_Fragment, { children: [_jsx(CommonDrawerLayout, { open: open, onClose: onClose, width: "xl", title: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx(Sparkles, { size: 16, color: "#6172F3" }), "Optimization Suggestions"] }), headerActions: messages.length > 0 ? (_jsx(HoverTooltip, { title: "Delete this conversation", placement: "bottom", children: _jsx(IconButton, { size: "small", onClick: () => setConfirmOpen(true), disabled: sending, "aria-label": "Delete conversation", children: _jsx(Trash2, { size: 15, color: "#71717A" }) }) })) : undefined, children: _jsx(Box, { sx: { height: '100%' }, children: body() }) }), _jsx(ConfirmActionModal, { open: confirmOpen, title: "Delete this conversation?", description: "This removes the analysis and every message in it. Reopening the ad will run a fresh analysis on the latest numbers, which takes about half a minute.", confirmText: "Delete", cancelText: "Cancel", loading: clearing, onConfirm: handleClear, onClose: () => setConfirmOpen(false) })] }));
};
export default AiSuggestionsDrawer;
